import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Bed, HeartPulse, Coffee, Dumbbell, Activity, UtensilsCrossed, ShoppingBasket, Music, Wind, X, Coins, ChevronRight, ArrowUpCircle, DoorOpen, Sparkles, Users, ArrowUpRight, ArrowDownRight, Minus, MessageCircle } from 'lucide-react';
import api from '../services/api';
import { useAvatar } from '../context/AvatarContext';
import nikkiAvatar from '../assets/nikki/nikki-sm.png';
import DistrictAtmosphere from '../components/district/DistrictAtmosphere';
import { scenerySrc } from '../lib/worldScenes';
import { timePhase, dailyWeather, healthWeather, PHASE_LABEL, WEATHER_LABEL } from '../lib/districtAtmosphere';

const PILLAR_CONFIG = {
  recovery: { title: 'Recovery District', accent: '#5FC6E8', worldWord: 'Recovery', buildings: [
    { id: 'bedroom',   label: 'Bedroom',          sub: 'Sleep sounds',       icon: Bed,        pos: { left: '38%', top: '46%' }, route: '/recovery?room=sounds', testid: 'building-hotspot-bedroom' },
    { id: 'lab',       label: 'Recovery Lab',     sub: 'Body map & numbers', icon: HeartPulse, pos: { left: '60%', top: '42%' }, route: '/recovery?room=clinic', testid: 'building-hotspot-sleep-clinic' },
    { id: 'community', label: 'Community Lounge', sub: 'The Village',        icon: Coffee,     pos: { left: '52%', top: '55%' }, route: '/village/recovery', testid: 'building-hotspot-community' },
  ]},
  movement: { title: 'Movement District', accent: '#A3E635', worldWord: 'Movement', buildings: [
    { id: 'gym',       label: 'Training Ground',  sub: "Today's session",    icon: Dumbbell,   pos: { left: '36%', top: '46%' }, route: '/workouts', testid: 'building-hotspot-gym' },
    { id: 'studio',    label: 'Mobility Studio',  sub: 'Range of motion',    icon: Activity,   pos: { left: '62%', top: '44%' }, route: '/mobility', testid: 'building-hotspot-studio' },
    { id: 'community', label: 'Community Lounge', sub: 'The Village',        icon: Coffee,     pos: { left: '52%', top: '56%' }, route: '/village/movement', testid: 'building-hotspot-community' },
  ]},
  nutrition: { title: 'Nutrition District', accent: '#FBBF24', worldWord: 'Nutrition', buildings: [
    { id: 'kitchen',   label: 'Kitchen',          sub: 'Log meals',          icon: UtensilsCrossed, pos: { left: '37%', top: '47%' }, route: '/nutrition', testid: 'building-hotspot-kitchen' },
    { id: 'market',    label: 'Market',           sub: 'Meals & macros',     icon: ShoppingBasket,  pos: { left: '62%', top: '44%' }, route: '/nutrition', testid: 'building-hotspot-market' },
    { id: 'community', label: 'Community Lounge', sub: 'The Village',        icon: Coffee,          pos: { left: '52%', top: '56%' }, route: '/village/nutrition', testid: 'building-hotspot-community' },
  ]},
  mental: { title: 'Mental District', accent: '#A78BFA', worldWord: 'Mind', buildings: [
    { id: 'sounds',     label: 'Sound Room',         sub: 'Soundscapes',       icon: Music,  pos: { left: '37%', top: '47%' }, route: '/mental', testid: 'building-hotspot-sounds' },
    { id: 'meditation', label: 'Meditation Pavilion', sub: 'Meditate & breathe', icon: Wind, pos: { left: '62%', top: '44%' }, route: '/mental', testid: 'building-hotspot-meditation' },
    { id: 'community',  label: 'Community Lounge',   sub: 'The Village',       icon: Coffee, pos: { left: '52%', top: '56%' }, route: '/village/mental', testid: 'building-hotspot-community' },
  ]},
};

// Buildings share art keyed by pillar+building; the "community" lounge art is shared across pillars.
const buildingArtKey = (pillar, bid) => (bid === 'community' ? 'community' : `${pillar}_${bid}`);

// Pillars using the NEW decoupled model: empty-plot land (5 stages) + building sprites
// that physically grow on their plot (tier 1→5).
const NEW_LAND_PILLARS = new Set(['recovery', 'movement', 'nutrition', 'mental']);
const LAND_PREFIX = { recovery: 'recland', movement: 'movland', nutrition: 'nutland', mental: 'mndland' };
const SPRITE_PREFIX = { recovery: 'recbld', movement: 'movbld', nutrition: 'nutbld', mental: 'mndbld' };
const LAND_W = 848;
const LAND_H = 1264;
const PLOT_POS = {
  recovery:  { bedroom: { left: '33%', top: '43%' }, lab: { left: '66%', top: '45%' }, community: { left: '48%', top: '66%' } },
  movement:  { gym: { left: '31%', top: '41%' }, studio: { left: '67%', top: '45%' }, community: { left: '47%', top: '69%' } },
  nutrition: { kitchen: { left: '31%', top: '41%' }, market: { left: '67%', top: '45%' }, community: { left: '47%', top: '69%' } },
  mental:    { sounds: { left: '31%', top: '41%' }, meditation: { left: '67%', top: '45%' }, community: { left: '47%', top: '69%' } },
};

const weatherFor = (s) => {
  if (s == null) return { label: 'Awaiting',  grad: 'linear-gradient(180deg, rgba(20,28,55,0.10) 0%, rgba(6,10,24,0.0) 45%, rgba(6,10,24,0.18) 100%)' };
  if (s >= 75)   return { label: 'Recharged', grad: 'linear-gradient(180deg, rgba(255,214,150,0.10) 0%, rgba(0,0,0,0) 50%, rgba(6,10,24,0.16) 100%)' };
  if (s >= 55)   return { label: 'Rested',    grad: 'linear-gradient(180deg, rgba(160,214,255,0.06) 0%, rgba(0,0,0,0) 50%, rgba(6,10,24,0.16) 100%)' };
  if (s >= 35)   return { label: 'Building',  grad: 'linear-gradient(180deg, rgba(120,130,150,0.10) 0%, rgba(20,24,40,0.04) 50%, rgba(6,10,24,0.20) 100%)' };
  return { label: 'Low', grad: 'linear-gradient(180deg, rgba(30,42,80,0.16) 0%, rgba(6,10,24,0.08) 45%, rgba(6,10,24,0.24) 100%)' };
};

const DRIVER_ICON = (i) => (i > 0 ? ArrowUpRight : i < 0 ? ArrowDownRight : Minus);
const DRIVER_COLOR = (i) => (i > 0 ? '#4ade80' : i < 0 ? '#f87171' : '#a1a1aa');

const DistrictView = () => {
  const navigate = useNavigate();
  const { pillar: rawPillar } = useParams();
  const pillar = PILLAR_CONFIG[rawPillar] ? rawPillar : 'recovery';
  const cfg = PILLAR_CONFIG[pillar];
  const ACCENT = cfg.accent;
  const { sceneSrc } = useAvatar() || {};

  const [iq, setIq] = useState(null);
  const [district, setDistrict] = useState(null);
  const [showAvatar, setShowAvatar] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [busy, setBusy] = useState(false);
  // Cinematic reveal: false = "Reflection" (avatar + Nikki front & center), true = "Gamified".
  const [isRevealed, setIsRevealed] = useState(false);

  const loadDistrict = useCallback(async () => {
    try { const r = await api.getDistrict(pillar); setDistrict(r.data); } catch (e) { /* keep */ }
  }, [pillar]);

  useEffect(() => {
    setDistrict(null);
    setIsRevealed(false);      // re-run the reveal for each pillar
    setSheet(null);
    setShowAvatar(false);
    api.getWellnessIQ().then(r => setIq(r.data)).catch(() => {});
    loadDistrict();
  }, [loadDistrict]);

  const score = iq?.sub_scores?.[pillar] ?? null;
  const insight = iq?.[`${pillar}_insight`];
  const drivers = (insight?.drivers || []).slice(0, 3);
  const weather = useMemo(() => weatherFor(score), [score]);
  const phase = useMemo(() => timePhase(), []);
  const sky = useMemo(() => healthWeather(score, pillar), [score, pillar]);
  const avatarImg = sceneSrc ? sceneSrc(pillar, score) : null;

  // Scene/avatar images can intermittently fail to load — fall back to the
  // character-free world scenery so we never show a broken-image icon.
  const onSceneImgError = useCallback((e) => {
    const el = e.currentTarget;
    if (el.dataset.fbApplied) return;
    el.dataset.fbApplied = '1';
    el.src = scenerySrc(pillar, score);
  }, [pillar, score]);

  // District map art occasionally drops on first paint — retry once with a
  // cache-busting query so the background reliably renders.
  const onMapImgError = useCallback((e) => {
    const el = e.currentTarget;
    if (el.dataset.retried) return;
    el.dataset.retried = '1';
    el.src = `${el.src.split('?')[0]}?r=${Date.now()}`;
  }, []);

  const coins = district?.coins_balance ?? 0;
  const land = district?.land || { key: 'rural', label: 'Rural', total_tiers: 0 };
  const districtLevel = district?.district_level ?? land.total_tiers ?? 0;
  const newLand = NEW_LAND_PILLARS.has(pillar);
  const mapSrc = newLand
    ? `/assets/living/districts/${LAND_PREFIX[pillar]}_${land.key}.png`
    : `/assets/living/districts/${pillar}_v_${land.key}.png`;

  const upgrade = async (buildingId) => {
    if (busy) return;
    setBusy(true);
    try {
      const prevLand = district?.land?.key;
      const r = await api.upgradeDistrictBuilding(pillar, buildingId);
      setDistrict(r.data);
      toast.success('Building upgraded!', { description: `+1 tier · ${r.data.coins_balance} coins left` });
      if (prevLand && r.data?.land?.key !== prevLand) toast('Your district is developing! 🌱', { description: `Now: ${r.data.land.label}` });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not upgrade');
    } finally { setBusy(false); }
  };

  const selected = sheet ? cfg.buildings.find(b => b.id === sheet) : null;
  const selData = selected && district?.buildings?.[selected.id];
  const selArtTier = selData?.art_tier ?? 0;
  const selHasArt = selArtTier >= 1;
  const selArtSrc = newLand
    ? `/assets/living/buildings/${SPRITE_PREFIX[pillar]}_${selected?.id}_t${Math.min(Math.max(selArtTier, 1), 5)}.png`
    : `/assets/living/buildings/${buildingArtKey(pillar, selected?.id)}_t${selArtTier}.png`;

  const stageStyle = {
    width: `min(100vw, calc(100vh * ${LAND_W} / ${LAND_H}))`,
    height: `min(100vh, calc(100vw * ${LAND_H} / ${LAND_W}))`,
  };

  return (
    <div className="fixed inset-0 overflow-hidden bg-[#0b1226]" data-testid="district-map-container">
      {newLand ? (
        <>
          <div className="absolute inset-0 overflow-hidden">
            <img src={mapSrc} alt="" aria-hidden draggable={false} onError={onMapImgError} className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-60 select-none pointer-events-none" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
            <div className="relative" style={stageStyle} data-testid="district-stage">
              <img src={mapSrc} alt="" draggable={false} onError={onMapImgError} className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none" />
            </div>
          </div>
        </>
      ) : (
        <div className="absolute inset-0" style={{ backgroundImage: `url(${mapSrc})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      )}
      <DistrictAtmosphere phase={phase} weather={sky} />
      <div className="absolute inset-0 pointer-events-none" style={{ background: weather.grad }} />

      {/* Building sprites — sit on the same contain-fit stage as the land (z-20, glow above weather) */}
      {newLand && (
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden z-20 pointer-events-none">
          <div className="relative" style={stageStyle}>
            {cfg.buildings.map((b) => {
              const Icon = b.icon;
              const tier = district?.buildings?.[b.id]?.tier ?? 0;
              const pos = PLOT_POS[pillar]?.[b.id] || b.pos;
              const spriteSrc = `/assets/living/buildings/${SPRITE_PREFIX[pillar]}_${b.id}_t${Math.min(Math.max(tier, 1), 5)}.png`;
              const size = 92 + Math.min(tier, 5) * 15;
              return (
                <button key={b.id} onClick={() => setSheet(b.id)} data-testid={b.testid}
                  className="absolute pointer-events-auto group flex flex-col items-center"
                  style={{ left: pos.left, top: pos.top, transform: 'translate(-50%, -78%)' }}>
                  {tier >= 1 ? (
                    <span className="relative flex items-center justify-center">
                      {phase === 'night' && (
                        <span aria-hidden className="absolute rounded-full" style={{ width: size * 0.95, height: size * 0.7, top: '20%', background: `radial-gradient(circle, ${ACCENT}55 0%, ${ACCENT}22 42%, transparent 70%)`, filter: 'blur(8px)' }} />
                      )}
                      <img src={spriteSrc} alt={b.label} draggable={false}
                        className="relative transition-transform duration-300 group-hover:scale-105 group-active:scale-95"
                        style={{ width: size, height: size, objectFit: 'contain', filter: 'drop-shadow(0 8px 10px rgba(0,0,0,0.5))' }} />
                    </span>
                  ) : (
                    <span className="w-14 h-14 rounded-full border-2 border-dashed grid place-items-center transition-transform group-hover:scale-110 animate-pulse"
                      style={{ borderColor: ACCENT, background: `${ACCENT}26` }}>
                      <Icon className="w-5 h-5 text-white/90" />
                    </span>
                  )}
                  <span className="mt-0.5 rounded-full bg-black/65 backdrop-blur px-2 py-0.5 border border-white/10 whitespace-nowrap">
                    <span className="block text-[10px] font-bold text-white leading-tight">{tier >= 1 ? `${b.label} · L${tier}` : `Tap to build ${b.label}`}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ===================== GAMIFIED HUD (after reveal) ===================== */}
      {/* Header */}
      <div className="absolute top-0 inset-x-0 z-30 flex items-start justify-between gap-3 px-4 pt-[calc(env(safe-area-inset-top,0px)+14px)]">
        {isRevealed ? (
          <button onClick={() => setShowAvatar(true)} data-testid="pip-avatar-btn"
            className="flex items-center gap-2 rounded-full border border-white/20 bg-black/40 backdrop-blur-xl pl-1.5 pr-3 py-1.5 hover:border-white/40 transition-colors">
            <motion.span layoutId="district-avatar" transition={{ type: 'spring', stiffness: 200, damping: 26 }}
              className="w-8 h-8 rounded-full overflow-hidden border shrink-0 block" style={{ borderColor: ACCENT }}>
              {avatarImg ? <img src={avatarImg} alt="You" onError={onSceneImgError} className="w-full h-full object-cover" /> : <span className="w-full h-full grid place-items-center bg-zinc-800 text-zinc-400 text-[9px]">You</span>}
            </motion.span>
            <span className="text-[10px] font-bold text-white">You</span>
          </button>
        ) : <span />}

        <AnimatePresence>
          {isRevealed && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ delay: 0.15 }} className="flex items-center gap-2">
              <div className="rounded-full border border-amber-300/40 bg-black/45 backdrop-blur-xl px-3 py-1.5 flex items-center gap-1.5" data-testid="district-coin-balance">
                <Coins className="w-4 h-4 text-amber-300" />
                <span className="text-sm font-bold text-white tabular-nums">{coins.toLocaleString()}</span>
              </div>
              <div className="rounded-full border bg-black/45 backdrop-blur-xl px-3 py-1.5 text-center" style={{ borderColor: `${ACCENT}66` }} data-testid="district-recovery-score">
                <span className="block text-[8px] uppercase tracking-[0.2em] text-white/70 leading-none">{pillar}</span>
                <span className="block text-base font-bold leading-tight" style={{ color: ACCENT }}>{score != null ? Math.round(score) : '—'}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Back + title */}
      <AnimatePresence>
        {isRevealed && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ delay: 0.2 }}
            className="absolute top-[calc(env(safe-area-inset-top,0px)+66px)] inset-x-0 z-20 flex flex-col items-center pointer-events-none">
            <button onClick={() => navigate('/dashboard')} data-testid="district-back"
              className="pointer-events-auto self-start ml-4 inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-black/30 backdrop-blur px-3 py-1.5 text-xs text-white/90 hover:border-white/40 transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Home
            </button>
            <div className="mt-2 rounded-2xl bg-black/40 backdrop-blur-sm px-5 py-2 text-center border border-white/10" data-testid="district-level-block">
              <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-white/75" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.85)' }}>{cfg.title}</span>
              <h1 className="font-secondary font-extrabold text-2xl text-white leading-none flex items-center justify-center gap-1.5" data-testid="district-level-display" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.85)' }}>
                <span className="text-sm font-semibold" style={{ color: ACCENT }}>Lv</span>{districtLevel}
              </h1>
              <span className="block mt-0.5 text-[10px] font-semibold text-white/85 tracking-wide" data-testid="district-land-label" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.85)' }}>{land.label} land · {PHASE_LABEL[phase]} · {WEATHER_LABEL[sky]}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Legacy pin hotspots (only for pillars not on the grow-in-place map) */}
      {!newLand && cfg.buildings.map((b) => {
        const Icon = b.icon;
        const tier = district?.buildings?.[b.id]?.tier ?? 0;
        return (
          <button key={b.id} onClick={() => setSheet(b.id)} data-testid={b.testid}
            className="absolute z-20 -translate-x-1/2 -translate-y-1/2 group flex flex-col items-center"
            style={{ left: b.pos.left, top: b.pos.top }}>
            <span className="relative w-11 h-11 rounded-2xl grid place-items-center border backdrop-blur-md transition-all duration-200 group-hover:scale-110 group-active:scale-95"
              style={{ borderColor: ACCENT, background: `${ACCENT}2E`, boxShadow: `0 0 22px ${ACCENT}99` }}>
              <Icon className="w-5 h-5 text-white" />
              <span className="absolute -top-1.5 -right-1.5 min-w-[17px] h-[17px] px-1 rounded-full bg-amber-400 text-black text-[10px] font-bold grid place-items-center border border-black/20">{tier}</span>
            </span>
            <span className="mt-1 rounded-full bg-black/60 backdrop-blur px-2 py-0.5 text-center border border-white/10">
              <span className="block text-[10px] font-bold text-white leading-tight">{b.label}</span>
            </span>
          </button>
        );
      })}

      {/* Village floating action button + compact Nikki dock (after reveal) */}
      <AnimatePresence>
        {isRevealed && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 24 }} transition={{ delay: 0.2 }}
            className="absolute bottom-0 inset-x-0 z-30 p-3 pb-[calc(env(safe-area-inset-bottom,0px)+12px)] flex items-end gap-2.5">
            <div className="flex-1 min-w-0 rounded-2xl border bg-black/60 backdrop-blur-xl p-2.5 flex gap-2.5 items-start" style={{ borderColor: `${ACCENT}44` }} data-testid="nikki-context-note">
              <img src={nikkiAvatar} alt="Nikki" className="w-8 h-8 rounded-full border object-cover shrink-0" style={{ borderColor: `${ACCENT}99` }} />
              <div className="min-w-0 flex-1">
                <p className="text-white text-[13px] font-semibold leading-snug">{insight?.headline || `Do a ${pillar} action to earn coins and build this district.`}</p>
                {insight?.action && <p className="text-[11px] text-zinc-300 mt-0.5 leading-snug">{insight.action}</p>}
              </div>
              <button onClick={() => navigate('/chat')} data-testid="district-talk-nikki" className="text-[11px] font-medium shrink-0 self-center flex items-center gap-0.5" style={{ color: ACCENT }}>
                Nikki <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <button onClick={() => navigate(`/village/${pillar}`)} data-testid="village-link-btn"
              className="shrink-0 rounded-2xl w-14 h-14 grid place-items-center border backdrop-blur-md transition-transform hover:scale-105 active:scale-95"
              style={{ borderColor: `${ACCENT}66`, background: `${ACCENT}26`, boxShadow: `0 0 22px ${ACCENT}55` }}
              aria-label="Enter the Village">
              <Users className="w-5 h-5" style={{ color: ACCENT }} />
              <span className="absolute -bottom-4 text-[8px] font-bold uppercase tracking-widest text-white/70">Village</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===================== REFLECTION OVERLAY (before reveal) ===================== */}
      <AnimatePresence>
        {!isRevealed && (
          <>
            {/* scrim — darkens + blurs the 3D world so the avatar + Nikki lead */}
            <motion.div key="scrim" data-testid="reflection-scrim"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}
              className="absolute inset-0 z-30 bg-black/55 backdrop-blur-2xl" />

            {/* hero reflection card */}
            <button onClick={() => navigate('/dashboard')} data-testid="reflection-back"
              className="absolute z-50 top-[calc(env(safe-area-inset-top,0px)+16px)] left-4 inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-black/40 backdrop-blur px-3.5 py-2 text-sm text-white hover:border-white/40 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Home
            </button>
            <motion.div key="hero" data-testid="reflection-overlay"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
              className="absolute inset-0 z-40 flex flex-col items-center px-6 pt-[calc(env(safe-area-inset-top,0px)+62px)] pb-[calc(env(safe-area-inset-bottom,0px)+16px)]">

              <span className="shrink-0 text-[11px] uppercase tracking-[0.3em] font-bold mb-3" style={{ color: ACCENT }}>{cfg.title}</span>

              {/* large avatar — the hero; morphs into the PIP on reveal via shared layoutId */}
              <motion.div layoutId="district-avatar" transition={{ type: 'spring', stiffness: 200, damping: 26, duration: 0.5 }}
                initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                className="shrink-0 relative w-60 h-60 sm:w-72 sm:h-72 rounded-3xl overflow-hidden border-4 shadow-2xl"
                style={{ borderColor: `${ACCENT}55`, boxShadow: `0 24px 70px -20px ${ACCENT}88` }}>
                {avatarImg
                  ? <img src={avatarImg} alt="You in this world" data-testid="hero-avatar-image" onError={onSceneImgError} className="w-full h-full object-cover" />
                  : <div className="w-full h-full grid place-items-center bg-zinc-900 text-zinc-500 text-sm">Your avatar</div>}
                <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: 'inset 0 -60px 60px -20px rgba(0,0,0,0.55)' }} />
              </motion.div>

              {/* score + state */}
              <div className="shrink-0 text-center mt-3">
                <div className="font-secondary font-black text-4xl leading-none text-white" style={{ textShadow: `0 0 34px ${ACCENT}88` }}>
                  {score != null ? Math.round(score) : '—'}
                </div>
                <span className="text-[11px] uppercase tracking-[0.28em] text-white/80">{cfg.worldWord} · {weather.label}</span>
              </div>

              {/* Nikki's read — scrolls internally so it never crowds out the avatar */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.5 }}
                className="flex-1 min-h-0 overflow-y-auto mt-4 w-full max-w-md rounded-2xl border bg-black/50 backdrop-blur-2xl p-5" style={{ borderColor: `${ACCENT}33` }} data-testid="reflection-nikki-read">
                <div className="flex items-center gap-2.5 mb-3">
                  <img src={nikkiAvatar} alt="Nikki" className="w-8 h-8 rounded-full object-cover border border-white/20" />
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/70">Nikki · your read today</span>
                </div>
                {insight ? (
                  <>
                    <p className="font-secondary font-bold text-lg leading-snug text-white" data-testid="reflection-headline">{insight.headline}</p>
                    {drivers.length > 0 && (
                      <div className="space-y-1.5 mt-3">
                        {drivers.map((d, i) => {
                          const Ico = DRIVER_ICON(d.impact);
                          return (
                            <div key={i} className="flex items-start gap-2" data-testid={`reflection-driver-${i}`}>
                              <Ico className="w-4 h-4 mt-0.5 shrink-0" style={{ color: DRIVER_COLOR(d.impact) }} />
                              <p className="text-[13px] text-zinc-300 leading-snug"><span className="text-white font-medium">{d.name}</span>{d.detail ? <span className="text-zinc-400"> — {d.detail}</span> : null}</p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {insight.action && (
                      <div className="mt-3 flex items-start gap-2.5 rounded-xl border px-4 py-3" style={{ borderColor: `${ACCENT}40`, background: `${ACCENT}14` }}>
                        <span className="text-[10px] font-mono uppercase tracking-widest mt-0.5 shrink-0" style={{ color: ACCENT }}>Do</span>
                        <p className="text-[13px] text-zinc-100 leading-snug" data-testid="reflection-action">{insight.action}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-zinc-300 leading-snug" data-testid="reflection-empty">Log a {cfg.worldWord.toLowerCase()} action or check in — this world wakes up as soon as there's data.</p>
                )}
                <button onClick={() => navigate('/chat')} data-testid="reflection-talk-nikki"
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: ACCENT }}>
                  <MessageCircle className="w-3.5 h-3.5" /> Talk to Nikki about this
                </button>
              </motion.div>

              {/* Awaken CTA */}
              <motion.button initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.5 }}
                onClick={() => setIsRevealed(true)} data-testid="awaken-district-btn"
                className="shrink-0 mt-4 group inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-bold text-black transition-transform hover:-translate-y-0.5 active:scale-95"
                style={{ background: `linear-gradient(90deg, ${ACCENT}, #ffffff)`, boxShadow: `0 14px 40px -10px ${ACCENT}` }}>
                <Sparkles className="w-5 h-5" /> Enter your {cfg.worldWord} world
                <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5" />
              </motion.button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Building sheet */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm" onClick={() => setSheet(null)} data-testid="building-sheet">
          <div className="w-full max-w-lg rounded-t-3xl border-t border-x bg-zinc-950/95 backdrop-blur-xl p-5 pb-[calc(env(safe-area-inset-bottom,0px)+20px)]" style={{ borderColor: `${ACCENT}44` }} onClick={(e) => e.stopPropagation()}>
            {selHasArt && (
              <div className="mb-3 -mt-1 flex items-center justify-center rounded-2xl py-2" style={{ background: `radial-gradient(circle at 50% 62%, ${ACCENT}26, transparent 70%)` }}>
                <img src={selArtSrc} alt={selected.label} data-testid="building-art-current" className="w-40 h-40 object-contain" style={{ filter: `drop-shadow(0 8px 20px ${ACCENT}66)` }} />
              </div>
            )}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-2xl grid place-items-center border" style={{ borderColor: ACCENT, background: `${ACCENT}22` }}>
                  <selected.icon className="w-5 h-5 text-white" />
                </span>
                <div>
                  <p className="font-secondary font-bold text-white text-lg leading-tight">{selected.label}</p>
                  <p className="text-xs text-white/60">Tier {selData?.tier ?? 0} · {selected.sub}</p>
                </div>
              </div>
              <button onClick={() => setSheet(null)} className="w-8 h-8 rounded-full bg-white/10 grid place-items-center text-white"><X className="w-4 h-4" /></button>
            </div>

            <button onClick={() => upgrade(selected.id)} disabled={busy || coins < (selData?.next_cost ?? Infinity)}
              data-testid="building-upgrade-btn"
              className="w-full rounded-2xl px-4 py-3.5 flex items-center justify-between font-bold text-black disabled:opacity-40 disabled:cursor-not-allowed transition-transform active:scale-[0.98]"
              style={{ background: 'linear-gradient(90deg,#FCD34D,#F59E0B)' }}>
              <span className="flex items-center gap-2"><ArrowUpCircle className="w-5 h-5" /> Upgrade to Tier {(selData?.tier ?? 0) + 1}</span>
              <span className="flex items-center gap-1"><Coins className="w-4 h-4" /> {selData?.next_cost?.toLocaleString()}</span>
            </button>
            {coins < (selData?.next_cost ?? 0) && (
              <p className="text-center text-xs text-amber-300/90 mt-2 flex items-center justify-center gap-1">
                <Sparkles className="w-3.5 h-3.5" /> Earn {(selData?.next_cost - coins).toLocaleString()} more coins to build this.
              </p>
            )}

            <button onClick={() => { setSheet(null); navigate(selected.route); }} data-testid="building-enter-btn"
              className="mt-3 w-full rounded-2xl border border-white/15 bg-white/5 px-4 py-3 flex items-center justify-center gap-2 text-sm font-medium text-white hover:border-white/30 transition-colors">
              <DoorOpen className="w-4 h-4" /> Enter {selected.label}
            </button>
          </div>
        </div>
      )}

      {/* Avatar portrait */}
      {showAvatar && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-sm p-6" onClick={() => setShowAvatar(false)} data-testid="avatar-portrait-modal">
          <div className="relative max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowAvatar(false)} className="absolute -top-3 -right-3 z-10 w-9 h-9 rounded-full bg-zinc-900 border border-white/20 grid place-items-center text-white"><X className="w-4 h-4" /></button>
            <div className="rounded-3xl overflow-hidden border" style={{ borderColor: `${ACCENT}66` }}>
              {avatarImg ? <img src={avatarImg} alt="Your avatar" onError={onSceneImgError} className="w-full object-cover" /> : <div className="aspect-square grid place-items-center bg-zinc-900 text-zinc-500">Avatar loading…</div>}
            </div>
            <p className="text-center text-white/70 text-xs mt-3">You in your {cfg.title.replace(' District','')} world today</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DistrictView;
